import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Coins, Gamepad2, Gift, Home, LineChart, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import BrandLogo from "@/components/BrandLogo";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { studentCopy } from "@/lib/studentI18n";

const NAV = [
  { to: "/s", labelKey: "home", icon: Home, end: true, testId: "nav-student-home", tone: "bg-violet-100 text-violet-700" },
  { to: "/s/progreso", labelKey: "progress", icon: LineChart, testId: "nav-student-progress", tone: "bg-cyan-100 text-cyan-700" },
  { to: "/s/juegos", labelKey: "games", icon: Gamepad2, testId: "nav-student-games", tone: "bg-amber-100 text-amber-800", gamingOnly: true },
  { to: "/s/recompensas", labelKey: "rewards", icon: Gift, testId: "nav-student-rewards", tone: "bg-pink-100 text-pink-700", gamingOnly: true },
];

export default function StudentShell() {
  const { principal, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [coins, setCoins] = useState(principal?.coins_balance ?? 0);
  const copy = studentCopy(principal?.preferred_language);

  useEffect(() => {
    refresh().then((me) => me && setCoins(me.coins_balance ?? 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gaming = principal?.gaming_mode;
  const items = NAV.filter((i) => !i.gamingOnly || gaming);

  const changeLanguage = async (preferredLanguage) => {
    try {
      const { data } = await api.patch("/student/preferences/language", {
        preferred_language: preferredLanguage,
      });
      await refresh();
      toast.success(
        data.packs_queued
          ? copy.languagePreparing
          : copy.languageUpdated
      );
    } catch (error) {
      toast.error(apiError(error, copy.languageError));
    }
  };

  return (
    <div className="student-shell min-h-screen pb-28">
      <header className="sticky top-0 z-30 border-b-2 border-violet-100/80 bg-white/88 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6">
          <BrandLogo to="/s" className="shrink-0" imageClassName="h-16 w-auto sm:h-20" data-testid="student-logo" />

          <nav className="ml-6 hidden items-center gap-1 lg:flex" aria-label={copy.mainNavigation}>
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `student-nav-link flex estudia-touch items-center gap-2 rounded-2xl px-4 text-sm font-extrabold ${
                    isActive ? "play-button bg-slate-900 text-white" : "text-slate-600 hover:bg-violet-50 hover:text-violet-700"
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                {copy[item.labelKey]}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <label className="sr-only" htmlFor="student-language">{copy.activityLanguage}</label>
            <select
              id="student-language"
              value={principal?.preferred_language || "es"}
              onChange={(event) => changeLanguage(event.target.value)}
              className="estudia-touch rounded-xl border-2 border-cyan-100 bg-cyan-50 px-2 text-sm font-extrabold text-cyan-800"
              data-testid="student-language-select"
              aria-label={copy.activityLanguage}
            >
              <option value="es">ES</option>
              <option value="en">EN</option>
            </select>
            {gaming && (
              <span
                className="flex items-center gap-1.5 rounded-2xl border-2 border-amber-200 bg-amber-100 px-3 py-2 text-sm font-extrabold text-amber-700 shadow-[0_4px_0_#f4bd30]"
                data-testid="student-coin-balance"
              >
                <Coins className="h-4 w-4" />
                {coins}
              </span>
            )}
            <Avatar config={principal?.avatar_config} name={principal?.name} size="sm" />
            <button
              onClick={() => {
                logout();
                navigate("/estudiante");
              }}
              className="grid h-11 w-11 place-items-center rounded-2xl text-slate-500 transition hover:-translate-y-0.5 hover:bg-rose-50 hover:text-rose-600"
              data-testid="student-logout-btn"
              aria-label={copy.logout}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:py-10">
        <Outlet context={{ onCoins: setCoins }} />
      </main>

      <nav
        className="fixed bottom-0 left-0 right-0 z-40 border-t-2 border-violet-100 bg-white/95 px-2 shadow-[0_-12px_36px_rgba(65,70,130,0.16)] backdrop-blur-xl"
        aria-label={copy.quickAccess}
        data-testid="student-bottom-nav"
      >
        <div className="mx-auto flex max-w-2xl">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`${item.testId}-bottom`}
              className={({ isActive }) =>
                `group my-2 flex min-w-0 flex-1 flex-col items-center gap-1 rounded-2xl px-1 py-1.5 text-[11px] font-extrabold transition sm:text-xs ${
                  isActive ? "-translate-y-1 bg-violet-100 text-violet-700 shadow-sm" : "text-slate-600 hover:-translate-y-0.5 hover:bg-violet-50 hover:text-violet-700"
                }`
              }
            >
              <span className={`grid h-10 w-10 place-items-center rounded-2xl shadow-sm transition-transform group-hover:scale-105 ${item.tone}`}>
                <item.icon className="h-6 w-6" />
              </span>
              {copy[item.labelKey]}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => {
              logout();
              navigate("/estudiante");
            }}
            className="group my-2 flex min-w-0 flex-1 flex-col items-center gap-1 rounded-2xl px-1 py-1.5 text-[11px] font-extrabold text-slate-600 transition hover:-translate-y-0.5 hover:bg-rose-50 hover:text-rose-600 sm:text-xs"
            data-testid="student-bottom-logout"
          >
            <span className="grid h-10 w-10 place-items-center rounded-2xl bg-rose-100 text-rose-600 shadow-sm transition-transform group-hover:scale-105">
              <LogOut className="h-6 w-6" />
            </span>
            {copy.logout}
          </button>
        </div>
      </nav>
    </div>
  );
}
